/**
 * PortfolioRadar — interactive 12-spoke SVG radar showing every EKSELENS360
 * sub-brand. Each spoke is a clickable hotspot that links into the matching
 * lite self-assessment. Built as a pure SVG (no chart library) so it stays
 * crisp at any size and gives us full control over hover behaviour.
 *
 * Data loading + geometry live in usePortfolioRadar; the share form and each
 * spoke are their own components.
 *
 * Used on /services to turn the services landing page into a discovery funnel.
 */
import React, { useState } from "react";
import { Send } from "lucide-react";
import usePortfolioRadar from "./portfolioRadar/usePortfolioRadar";
import PortfolioShareModal from "./portfolioRadar/PortfolioShareModal";
import RadarSpoke from "./portfolioRadar/RadarSpoke";
import {
  RING_LEVELS, VB_W, VB_H, CENTER_X, CENTER_Y, RADIUS,
} from "./portfolioRadar/constants";

export default function PortfolioRadar() {
  const [hovered, setHovered] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const {
    snapshots, completedCount, personalised,
    spokes, polygonPoints, peerHigh, peerLow, peerBandCovered, reset,
  } = usePortfolioRadar();

  return (
    <div className="w-full" data-testid="portfolio-radar">
      {personalised ? (
        <div className="flex items-center justify-between gap-3 mb-4 px-3 py-2.5 bg-[#7737F6]/12 border border-[#7737F6]/40 text-[12px] flex-wrap" data-testid="portfolio-radar-personalised-banner">
          <span className="text-[#A179FA] tracking-wide">
            <strong className="text-white font-semibold">Your maturity</strong> · {completedCount} of 12 diagnostics completed — radar shows your actual scores.
            {peerBandCovered >= 4 ? (
              <span className="ml-2 inline-flex items-center gap-1 text-[#A179FA] text-[10.5px] tracking-wide" data-testid="portfolio-radar-peer-legend">
                · Shaded band = peer 25–75th percentile ({peerBandCovered} sub-brands)
              </span>
            ) : null}
          </span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              className="inline-flex items-center gap-1.5 bg-[#7737F6] hover:bg-[#5E22D8] text-white px-3 py-1.5 text-[11.5px] font-semibold tracking-wide transition-colors"
              data-testid="portfolio-radar-share"
            >
              <Send size={12} /> Send my portfolio to Ekselens
            </button>
            <button type="button" onClick={reset} className="text-zinc-400 hover:text-white underline underline-offset-2" data-testid="portfolio-radar-reset">Reset</button>
          </div>
        </div>
      ) : null}

      <PortfolioShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        snapshots={snapshots}
        completedCount={completedCount}
      />

      <svg
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        className="w-full h-auto block"
        role="img"
        aria-label="EKSELENS360 portfolio radar — twelve sub-brands"
      >
        {/* Concentric rings */}
        {Array.from({ length: RING_LEVELS }, (_, i) => i + 1).map((lvl) => (
          <circle
            key={lvl}
            cx={CENTER_X}
            cy={CENTER_Y}
            r={(RADIUS / RING_LEVELS) * lvl}
            fill="none"
            stroke="rgba(255,255,255,0.10)"
            strokeWidth={1}
          />
        ))}

        {/* Spoke lines */}
        {spokes.map((s) => (
          <line
            key={`line-${s.code}`}
            x1={CENTER_X}
            y1={CENTER_Y}
            x2={s.tip.x}
            y2={s.tip.y}
            stroke="rgba(255,255,255,0.12)"
            strokeWidth={1}
          />
        ))}

        {/* Peer benchmark band — p25 → p75 shaded ring */}
        {peerBandCovered >= 4 ? (
          <g data-testid="portfolio-radar-peer-band">
            <polygon points={peerHigh} fill="rgba(161,121,250,0.13)" stroke="rgba(161,121,250,0.45)" strokeWidth={1} strokeDasharray="3 3" />
            <polygon points={peerLow} fill="#FFFFFF" stroke="rgba(161,121,250,0.35)" strokeWidth={1} strokeDasharray="3 3" />
          </g>
        ) : null}

        {/* Brand-purple "alive" polygon */}
        <polygon points={polygonPoints} fill="rgba(119,55,246,0.22)" stroke="#A179FA" strokeWidth={2} />

        {/* Center mark — EKSELENS360 */}
        <circle cx={CENTER_X} cy={CENTER_Y} r={56} fill="#0B0B0C" stroke="#7737F6" strokeWidth={1.5} />
        <text x={CENTER_X} y={CENTER_Y - 4} textAnchor="middle" className="fill-white" fontSize={20} fontWeight={700} letterSpacing={2}>
          EKSELENS360
        </text>
        <text x={CENTER_X} y={CENTER_Y + 18} textAnchor="middle" className="fill-[#A179FA]" fontSize={10} letterSpacing={3}>
          {personalised ? `${completedCount} / 12 COMPLETED` : "MATURITY ENGINE"}
        </text>

        {/* Per-spoke: tip dot + hotspot link + label */}
        {spokes.map((s) => (
          <RadarSpoke key={`spoke-${s.code}`} s={s} active={hovered === s.code} onHover={setHovered} />
        ))}
      </svg>
    </div>
  );
}
